import { useEffect, useRef, useState } from 'react';
import {
  Bookmark,
  FileText,
  FolderOpen,
  Loader2,
  MessageSquare,
  Paperclip,
  Search,
  X,
} from 'lucide-react';
import {
  useWorkspace,
  type WorkspaceSearchHit,
} from '@/hooks/useWorkspace';
import { cn } from '@/lib/utils';

function hitIcon(kind: WorkspaceSearchHit['matchKind']) {
  switch (kind) {
    case 'memory':
      return Bookmark;
    case 'context':
      return FileText;
    case 'project':
      return FolderOpen;
    case 'attachment':
      return Paperclip;
    default:
      return MessageSquare;
  }
}

interface ChatSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (hit: WorkspaceSearchHit) => void;
}

export function ChatSearchDialog({ open, onClose, onSelect }: ChatSearchDialogProps) {
  const { searchWorkspace } = useWorkspace();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<WorkspaceSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHits([]);
      setActive(0);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      void searchWorkspace(q, 40)
        .then((r) => {
          setHits(r);
          setActive(0);
        })
        .finally(() => setLoading(false));
    }, 180);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, searchWorkspace]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, Math.max(0, hits.length - 1)));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter' && hits[active]) {
        e.preventDefault();
        onSelect(hits[active]!);
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hits, active, onClose, onSelect]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[10vh] sm:pt-[12vh] px-4">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px]" onClick={onClose} />
      <div
        role="dialog"
        aria-label="Search chats"
        className="relative w-full max-w-lg bg-[#141414] border border-[#2a2a2a] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[min(70vh,520px)] od-enter"
      >
        <div className="flex items-center gap-2.5 px-3.5 py-3.5 border-b border-[#222]">
          <Search className="w-4 h-4 text-[#5a5a5a] shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats, messages, memory, context…"
            className="flex-1 bg-transparent text-[15px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#5a5a5a]" />}
          <kbd className="hidden sm:inline text-[10px] text-[#3a3a3a] font-mono px-1.5 py-0.5 rounded border border-[#222]">
            esc
          </kbd>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]"
            aria-label="Close search"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 py-1 scrollbar-thin">
          {!query.trim() && (
            <p className="px-4 py-8 text-center text-[13px] text-[#4a4a4a]">
              Search titles, messages, project memory, and context packs
            </p>
          )}
          {query.trim() && !loading && hits.length === 0 && (
            <p className="px-4 py-8 text-center text-[13px] text-[#4a4a4a]">
              No matches
            </p>
          )}
          {hits.map((hit, i) => {
            const Icon = hitIcon(hit.matchKind);
            return (
            <button
              key={`${hit.chatId ?? hit.projectId}-${hit.messageId ?? hit.matchKind ?? 't'}-${i}`}
              type="button"
              onClick={() => {
                onSelect(hit);
                onClose();
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'w-full text-left px-3 py-2.5 flex gap-3 transition-colors',
                i === active ? 'bg-[#1e1e1e]' : 'hover:bg-[#1a1a1a]',
              )}
            >
              <Icon className="w-4 h-4 text-[#4a4a4a] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[13px] font-medium text-[#e8e6e3] truncate">
                    {hit.chatTitle}
                  </span>
                  {hit.projectName && hit.chatId && (
                    <span className="text-[11px] text-[#4a4a4a] truncate shrink-0">
                      {hit.projectName}
                    </span>
                  )}
                  {hit.matchKind && hit.matchKind !== 'message' && hit.matchKind !== 'title' && (
                    <span className="text-[10px] uppercase tracking-wide text-[#3a3a3a] shrink-0">
                      {hit.matchKind}
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-[#6a6a6a] truncate mt-0.5">
                  {hit.snippet}
                </p>
              </div>
            </button>
            );
          })}
        </div>

        <div className="px-3 py-2 border-t border-[#242424] text-[10px] text-[#3a3a3a] flex gap-3">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
